/**
 * Category Cockpit — the actions of the "To decide" queue (band B).
 *
 * `decisionActs(d)` turns one question's `act` descriptor into the buttons that answer it. The
 * descriptor comes from the backend and names the ENTITY; the endpoint is chosen here, once.
 *
 * ⛔ A QUESTION WITH NO `act` GETS NO BUTTON, AND THAT IS THE ANSWER. Five of the ten groups are
 * aggregates over thousands of rows — "20,845 discovered gaps were never suggested" is not a thing
 * one click should do — and they keep the link to the page that owns the review. Inventing a
 * "do it all" button for them would be the opposite of what the queue is for.
 */
import React from 'react';
import * as A from '@/services/categoryCockpitActions';
import type { Decision } from '@/services/categoryCockpitService';
import type { ActionDef } from '../components/Act';
import type { ActionResult } from '@/services/categoryCockpitActions';
import { fmt, leaf, plural } from './format';
import { AccountingSaid, COMMUNITY_DISCOVERY, SILENT_REBUILD_FAILURE, researchAllOp, sectorPersonaRebuild } from './actions';

/**
 * Every button of one question, each carrying what it is ABOUT — the sector, the category, the
 * site and the queued row. The To-decide queue lists questions of many categories side by side,
 * and its results land in a log that outlives the screen: a result that does not name its target
 * is attributed to whichever question the reader happens to be looking at.
 */
export function decisionActs(d: Decision, sectorId: number, sectorName?: string): ActionDef[] {
  const target = {
    sector: sectorName || `sector ${sectorId}`,
    ...(d.path ? { path: d.path } : {}),
    ...(d.site ? { site: d.site } : {}),
    item: d.id,
  };
  return questionActs(d, sectorId, sectorName).map((def) => ({ ...def, target: def.target || target }));
}

/**
 * A knowledge draft (band D): a change a person proposed from the cockpit, written only when a
 * super admin approves it here. Approve runs the draft's handler through the same executor as any
 * agent action — so the same caveat holds: the row is marked executed even when the handler
 * refuses (a changed base, a non-super-admin approver), and the handler's own answer is read back.
 */
function draftActs(d: Decision, act: Extract<NonNullable<Decision['act']>, { kind: 'agent_action' }>): ActionDef[] {
  return [
    {
      label: d.labels[0],
      confirm: {
        title: 'Approve this draft',
        verb: 'Approve',
        danger: act.destructive,
        writes: <>the drafted change reaches the category: it <b>{act.effect}</b>. The draft row in <code>agent_actions</code> is marked executed with your user id.</>,
        cost: act.spends ? <>approving runs research on the concept — model calls, not reported back to this screen.</> : undefined,
        warn: (
          <>
            {act.destructive ? <><b>This replaces what is stored.</b>{' '}</> : null}
            The handler refuses if you are not a super admin, or if the aspect changed since the draft was made — <b>and the row is marked executed either way</b>, so what the handler reported is read back below. A refused draft has to be drafted again.
          </>
        ),
      },
      run: () => A.executeAgentAction(act.id),
      render: (r) => {
        const o = A.handlerOutcome(r);
        return (
          <>
            <div className={o.ok === false ? 'pmk-warn' : undefined}>
              {o.ok === true ? 'Approved and written.' : o.ok === false ? 'The draft was REFUSED by its handler — nothing was written, and the draft is closed.' : 'The handler reported nothing conclusive.'}
            </div>
            <div className="pmc-small pmc-mute">{o.detail}</div>
          </>
        );
      },
    },
    {
      label: d.labels[1],
      confirm: {
        title: 'Reject this draft',
        verb: 'Reject',
        writes: <>the draft in <code>agent_actions</code> is marked dismissed, with your reason and your user id. Nothing is written to the category.</>,
        ask: { label: 'Why is it rejected?', placeholder: 'e.g. the mapping is already covered by "cushioning_level"', min: 3 },
      },
      run: (reason) => A.dismissAgentAction(act.id, reason),
    },
  ];
}

function questionActs(d: Decision, sectorId: number, sectorName?: string): ActionDef[] {
  const act = d.act;
  if (!act) return [];

  switch (act.kind) {
    case 'agent_action':
      if (act.draft) return draftActs(d, act);
      return [
        {
          label: d.labels[0],
          confirm: {
            title: `Execute the ${act.action_type} action`,
            verb: 'Execute',
            danger: act.destructive,
            // ⛔ NAME THE EFFECT, NOT THE HANDLER. "It runs the handler" authorised, among others,
            // a pass that strips benefit_scores from every product of the category and one that
            // flips PUBLISHED pages to stale — both of which arrived here as `destructive: false`
            // with no spend disclosed. `effect` is read from the handler's own code by the backend
            // (categoryDerive AGENT_ACTIONS), and an action_type it has not read gets no button at
            // all rather than this sentence with a guess in it.
            writes: (
              <>
                it runs the <code>{act.action_type}</code> handler and marks the row executed in <code>agent_actions</code>. Executing it <b>{act.effect || 'does whatever that handler does — this cockpit has not read it'}</b>.
              </>
            ),
            cost: act.spends ? <>this handler makes model calls, or queues a job that will. The spend is not reported back to this screen.</> : undefined,
            warn: (
              <>
                {act.destructive ? <><b>This is destructive: {act.effect}</b>, and nothing here undoes it.{' '}</> : null}
                <b>And the row is marked executed whether or not the handler worked.</b> The service swallows a handler failure and still records the execution, so what the handler actually reported is read back from the reply&apos;s <code>executionResult</code> and shown below — the envelope alone says nothing.
              </>
            ),
          },
          run: () => A.executeAgentAction(act.id),
          render: (r) => {
            const o = A.handlerOutcome(r);
            return (
              <>
                <div className={o.ok === false ? 'pmk-warn' : undefined}>
                  {o.ok === true ? 'The handler reported success.' : o.ok === false ? 'The handler FAILED — the action is still marked executed.' : 'The handler reported nothing conclusive.'}
                </div>
                <div className="pmc-small pmc-mute">{o.detail}</div>
              </>
            );
          },
        },
        {
          label: d.labels[1],
          confirm: {
            title: 'Dismiss this action',
            verb: 'Dismiss',
            writes: <>the row in <code>agent_actions</code> is marked dismissed, with your reason and your user id. The handler does not run.</>,
            ask: { label: 'Why is it dismissed?', placeholder: 'e.g. superseded by the feed re-import of 2026-09-20', min: 3 },
          },
          run: (reason) => A.dismissAgentAction(act.id, reason),
        },
      ];

    case 'apply_gaps': {
      // ⛔ THE COUNT ON THE SCREEN IS THIS SECTOR'S; THE BUTTON IS THE WHOLE SITE.
      // `applyAllApprovedMappingsForSite` selects `WHERE site_id = $1` and groups by
      // (sector_id, category_path), so every sector the site carries is written. `n_all_sectors`
      // is that scope's own count, measured without the sector predicate; `null` means it was not
      // measured, which is said rather than papered over with the sector figure.
      const wider = act.n_all_sectors != null && act.n_all_sectors > act.n;
      return [{
        label: d.labels[0],
        confirm: {
          title: `Apply the approved gaps of ${d.site}`,
          verb: 'Apply',
          danger: wider,
          writes: (
            <>
              the approved mappings of <b>{d.site}</b> — every category, not only one — into <code>category_characteristics</code>, each with a <code>category_research_event</code>.
              {' '}
              {act.n_all_sectors == null
                ? <><b>Across every sector this site has</b>, not only the one on screen; the all-sector count was not measured.</>
                : wider
                  ? <><b>ACROSS EVERY SECTOR THIS SITE HAS: {fmt(act.n_all_sectors)} appliable rows{act.sectors ? ` in ${act.sectors} sectors` : ''}</b>, of which {fmt(act.n)} are in the sector on screen. The other {fmt(act.n_all_sectors - act.n)} are in categories this screen never showed you.</>
                  : <>Every sector this site has, which here is the sector on screen: {fmt(act.n_all_sectors)} appliable rows.</>}
            </>
          ),
          // ⛔ THE SAME UNDISCLOSED SPEND AS THE PER-CATEGORY APPLY, OVER A WHOLE SITE.
          // `applyApprovedMappings` researches the spec cache for each new canonical attribute
          // (backend-merged/services/SemanticGapService.js:3956 @ 52b41e15b). This button runs it
          // for every category of the site at once, and the reply reports nothing at all — so the
          // number of calls is not knowable here even after the fact.
          cost: <>none for a plain mapping. <b>Each approved <code>attribute_discovery</code> gap costs one research call</b>, and this button covers every category of the site — so the spend is however many discoveries are in that queue. The reply does not report them, nor the calls: this route answers <code>202</code> and the work happens in a job.</>,
          warn: <>{fmt(act.n)} rows are ready in this sector by this cockpit&apos;s count; Apply applies what IT accepts, which can be fewer. <b>The reply does not say how many</b> — this route records a job and answers <code>202</code> with its id, so the outcome is read on the Semantic Gaps page, not here. Discovery gaps also write <code>canonical_attributes</code>, which is the category schema every site shares, not this site&apos;s copy. The products carry the new values only after a re-enrichment.</>,
        },
        run: () => A.applyGapsForSite(act.site_id),
        // ⛔ THIS ROUTE NEVER REPORTS COUNTS, SO THE RENDERER MUST NOT PROMISE THEM.
        // `POST /api/semantic-gaps/apply-all/:siteId` answers `202 {success, jobId}` and does the
        // work in a job; `applied` is never in that body, so the old fallback printed the literal
        // "applied ?" — a number-shaped blank where an operator reads a result. What is knowable at
        // this point is the job id and the fact that the queue below has not moved yet, so that is
        // what it says. (The per-category Apply on the category screen is a DIFFERENT route and is
        // synchronous: it does return `applied`, and its own renderer prints it.)
        render: (r) => (
          <>
            <div>{String(r.body.message || 'Queued.')} The counts are not in this reply: the route records a job and applies the mappings behind it.</div>
            {r.body.jobId ? <div className="pmc-small pmc-mute">job {String(r.body.jobId)} — follow it on the Semantic Gaps page</div> : null}
            <div className="pmc-small pmc-mute">This question stays in the list until the job finishes and the queue is read again.</div>
          </>
        ),
      }];
    }

    case 'split':
      return [
        {
          label: d.labels[0],
          confirm: {
            title: `Approve the split of ${leaf(String(d.path || ''))}`,
            verb: 'Approve the split',
            danger: true,
            writes: <>the proposal is approved and the category&apos;s products are RE-CLASSIFIED into {act.children ? plural(act.children, 'new sub-category') : 'the proposed sub-categories'}.</>,
            warn: <><b>This is the one decision in the cockpit that resumes a process by itself.</b> Pages hang from the new children afterwards. A reply of <code>202</code> means the approval committed and the split application did NOT — and nothing finishes it on its own.</>,
            ask: { label: 'Note for the record (optional)', placeholder: 'why this split is right', min: 0 },
          },
          run: (notes) => A.decideSplit(act.site_id, act.id, 'approve_split', notes || undefined),
          render: (r) => <SplitSaid r={r} />,
        },
        {
          label: d.labels[1],
          confirm: {
            title: 'Reject the split',
            verb: 'Reject',
            writes: <>the proposal is recorded as rejected. The category is not touched and its products stay where they are.</>,
            ask: { label: 'Why is the split rejected?', placeholder: 'at least three characters — it is the audit of the released gate', min: 3 },
          },
          // reject_split records the HITL decision; dismiss closes the proposal outright. The
          // queue's question is about the decision, so it is the decision that is recorded.
          run: (notes) => A.decideSplit(act.site_id, act.id, 'reject_split', notes),
        },
      ];

    case 'key_match':
      return [
        {
          label: d.labels[0],
          confirm: {
            title: `Attach the catalogue to "${act.proposed_key}"`,
            verb: 'Accept the key',
            writes: <>the proposal is accepted with the key <code>{act.proposed_key}</code>, and the catalogue is attached to that research key — which decides which knowledge enrichment and generation will use for it. <b>And it does not stop there:</b> the accept enqueues a catalogue-activation job for the whole site, which is what resumes the site&apos;s category research.</>,
            // ⛔ AN ACCEPT THAT STARTS A PAID SITE-WIDE JOB, DESCRIBED AS ATTACHING A LABEL.
            // `CategoryKeyMatchService.decide()` calls `enqueueCatalogActivationJob(row.site_id)`
            // once the acceptance commits (backend-merged/services/CategoryKeyMatchService.js:421
            // @ 4bf43259c), and the worker then calls
            // `CategoryResearchService.researchCategoriesForSite(...)`
            // (backend-merged/services/CatalogActivationWorker.js:882 @ 4bf43259c). The spend
            // happens later, in another process, which is exactly why no cost line was here.
            cost: <><b>model calls, asynchronously and for the whole site.</b> The job this enqueues runs the site&apos;s category research in a worker, so the spend is neither made nor reported while you wait — the reply below tells you the job was queued, never what it cost. The enqueue is idempotent: an in-flight job for this site is not duplicated.</>,
            reach: <>the site, not this category: the activation job researches the site&apos;s categories, not only the one this question is about.</>,
            warn: <>The key sent is the one displayed here. If the stored proposal has changed since this screen was read, the endpoint refuses with <code>proposal_changed</code> and names both keys rather than accepting something you did not see.</>,
          },
          run: () => A.acceptKeyProposal(act.id, act.proposed_key),
          render: (r) => <ActivationSaid r={r} />,
        },
        {
          label: d.labels[1],
          confirm: {
            title: 'Refuse this key',
            verb: 'Refuse',
            writes: <>the proposal is marked rejected. The catalogue stays unattached and unresearched until another key is proposed.</>,
          },
          run: () => A.rejectKeyProposal(act.id),
        },
      ];

    case 'key_match_reject_only':
      return [{
        label: d.labels[1],
        confirm: {
          title: 'Refuse this key',
          verb: 'Refuse',
          writes: <>the proposal is marked rejected. There is no proposed key to accept — accepting requires one, and the endpoint refuses an empty confirmation.</>,
        },
        run: () => A.rejectKeyProposal(act.id),
      }];

    case 'research_retry':
      return [{
        label: 'Research it again',
        // the category page's "Research all 10 aspects" is this same call: one guard for both
        op: researchAllOp(sectorId, act.path),
        confirm: {
          title: `Re-run the research of ${leaf(act.path)}`,
          verb: 'Research',
          // the SAME endpoint as the category page's "Research all 10 aspects", so the SAME disclosure
          // of the sector rebuild, quoted rather than re-worded (round 14: this entry point was the
          // second one describing category-local writes)
          writes: <>all ten aspect columns of <b>{leaf(act.path)}</b>, replacing what the failed run left behind.{COMMUNITY_DISCOVERY.writes}{sectorPersonaRebuild(sectorName).writes}</>,
          reach: sectorPersonaRebuild(sectorName).reach,
          cost: <>ten model calls and one attribute-intelligence run, synchronously.{COMMUNITY_DISCOVERY.cost} No portraits are generated on this path.</>,
          warn: <>Nothing here changes the sample size or includes the children — this is the same call that failed. If the failure was the sampler refusing the category, it will refuse again, and say so. {SILENT_REBUILD_FAILURE}</>,
        },
        run: () => A.researchAllAspects(sectorId, act.path),
        // ⛔ THE SAME ENDPOINT, SO THE SAME READING OF ITS REPLY. This is the route whose
        // `accounting.status` is about the MONEY and not about the research: a run that called the
        // model and then failed answers 200, `status: 'recorded'`, `research_error` set, and the
        // fixed message "Research completed successfully". Without this the inbox fell back to the
        // default renderer, printed that message under a green heading, and left the failure in the
        // collapsed reply — the exact defect the category screen fixed, alive on the other screen
        // because the renderer was attached to a button rather than to the endpoint.
        render: (r) => <AccountingSaid r={r} />,
      }];

    default:
      return [];
  }
}


/**
 * ⛔⛔ TWO OUTCOMES IN ONE 200, AND ONLY THE FIRST ONE WAS BEING READ.
 *
 * `decide()` commits the acceptance and THEN asks the worker for an activation job. An enqueue that
 * throws must not turn a committed decision into a 500 the screen reads as "refused" — so the route
 * answers 200 `success: true` and reports the second outcome beside it in `activation`
 * (backend-merged/services/CategoryKeyMatchService.js:421-439 @ 4bf43259c):
 *   · `queued` with a `jobId` — the research will run;
 *   · `enqueue_failed` — that transaction is KNOWN to have rolled back: no job;
 *   · `enqueue_unknown` — a lost COMMIT acknowledgement: a job may or may not exist;
 *   · `migration_pending` — the enqueue was dropped because the schema is not ready.
 * With no renderer the default printed the acceptance and put all four inside collapsed JSON, so
 * "the catalogue is attached and nothing will research it" read exactly like success. The per-tick
 * reconciler settles the last three eventually, and saying so is part of the answer — it is the
 * difference between "act now" and "it is being handled".
 */
function ActivationSaid({ r }: { r: ActionResult }) {
  const a = (r.body.activation || null) as Record<string, unknown> | null;
  const status = a ? String(a.status || '') : '';
  const jobId = a && a.jobId ? String(a.jobId) : '';
  const detail = a && a.detail ? String(a.detail) : '';
  const bad = status === 'enqueue_failed' || status === 'migration_pending';
  const unsure = status === 'enqueue_unknown';
  return (
    <>
      <div>The key is accepted and the catalogue is attached.</div>
      {!a ? (
        <div className="pmc-small pmc-mute">The reply carried no <code>activation</code>, so nothing here says whether the research job was queued.</div>
      ) : bad ? (
        <div className="pmk-warn" style={{ marginTop: 4 }}>
          <b>But the activation job was NOT queued ({status}).</b> The acceptance stands; the site&apos;s research will not start from it until the reconciler backfills the job.{detail ? <> The server said: {detail}</> : null}
        </div>
      ) : unsure ? (
        <div className="pmk-warn" style={{ marginTop: 4 }}>
          <b>And whether the activation job was queued is UNKNOWN.</b> The enqueue&apos;s commit acknowledgement was lost, so a job may or may not exist — do not read this as "not queued", and do not accept again to force one. The reconciler settles it either way.{detail ? <> The server said: {detail}</> : null}
        </div>
      ) : (
        <div className="pmc-small pmc-mute">The activation job is queued{jobId ? <> — job <code>{jobId}</code></> : null}; the site&apos;s category research runs in a worker from here.</div>
      )}
    </>
  );
}


/**
 * ⛔⛔ "ACCEPTED, NOT FINISHED" IS THE STATUS CODE'S OPINION, NOT THE ROUTE'S.
 *
 * `approveHitl` commits the approval — children materialised, proposal promoted to `proposed`,
 * decision recorded — and then applies the split. When the application hits a KNOWN gate the route
 * answers `202 { success: true, partial: true, …result }` with `applyFailed: { message, code }`
 * (backend-merged/routes/category-granularity.js:162 and
 * backend-merged/services/CategoryGranularityService.js:2170,2188 @ 4bf43259c).
 *
 * `<Act>` reads 202 as "accepted, not finished", which is true and is not the whole truth: nothing
 * in the system finishes this one. There is no worker behind the 202 — the operator has to
 * remediate the now-`proposed` proposal through dry-run/apply — and the recorded decision takes the
 * question OUT of this queue, so the screen stops mentioning it at all. The default renderer put
 * the reason inside collapsed JSON (`ServerSaid` skips nested objects), which is where a
 * half-finished split then sat until somebody noticed the products had not moved.
 */
function SplitSaid({ r }: { r: ActionResult }) {
  const failed = (r.body.applyFailed || null) as Record<string, unknown> | null;
  const children = typeof r.body.children === 'number' ? (r.body.children as number) : null;
  if (!failed) {
    return (
      <div>
        The split is approved and applied{children != null ? <> — {plural(children, 'sub-category')} now carry the products</> : null}.
      </div>
    );
  }
  return (
    <>
      <div>The approval COMMITTED: the sub-categories exist and the decision is recorded.</div>
      <div className="pmk-warn" style={{ marginTop: 4 }}>
        <b>But the split was NOT applied — the products have not moved.</b>{failed.code ? <> <code>{String(failed.code)}</code>.</> : null}{failed.message ? <> {String(failed.message)}</> : null}
        {' '}Nothing finishes this on its own and this question leaves the queue now that the decision is recorded, so it is on you: remediate the proposal — which is now <code>proposed</code> — through dry-run/apply on the category-splits page. Approving again answers <code>409</code>, because it has left <code>needs_hitl</code>.
      </div>
    </>
  );
}
